import React, {useMemo} from 'react';
import useTranslation from "~/app/hooks/useTranslation";
import classes from "./style.module.scss";
import {useStoreState} from '~/store';
import Icon from '~/app/common/Icon';
import moment from 'moment';
import { PERIODIC_CHECK_DRAFT_STATUS } from '~/app/data/statuses/periodicCheck';
import { InspectionTypes } from '~/periodicCheck/store';
import { LastPeriodicCheck, Machine } from '~/types';
import { isPeriodicCheckAwaitingSync, usePendingPeriodicChecks } from '~/periodicCheck/usePendingPeriodicChecks';
const resolveLastRegisteredCheck = (
    machine: Machine,
    checkEquipment: { id: Machine['id'] } | null,
    inspectionType: number,
): LastPeriodicCheck | undefined => {
    const equipmentId = checkEquipment?.id ?? machine.id;
    const source =
        String(equipmentId) === String(machine.id)
            ? machine
            : machine.linkedMachines?.find(l => String(l.id) === String(equipmentId));
    if (!source) {
        return undefined;
    }
    return inspectionType === InspectionTypes.ACCEPTANCE_INSPECTION
        ? source.lastAcceptanceInspection
        : source.lastDailyCheck;
};
const isDraftStatus = (status?: number) => Number(status) === PERIODIC_CHECK_DRAFT_STATUS;

export type HeaderComponent = React.FC

const Header: HeaderComponent = () => {
    const {t} = useTranslation("periodicCheck");
    const {t: tDetails} = useTranslation("machine.details");
    const inspectionType = useStoreState(state => state.periodicCheck.inspectionType);
    const machine = useStoreState(state => state.machine.data);
    const checkEquipment = useStoreState(state => state.periodicCheck.checkEquipment);
    const sessionMode = useStoreState(state => state.periodicCheck.sessionMode);
    const lastRegisteredCheck = useMemo(
        () => resolveLastRegisteredCheck(machine, checkEquipment, inspectionType),
        [machine, checkEquipment, inspectionType],
    );
    const equipmentId = checkEquipment?.id ?? machine.id;
    const { pending, loaded } = usePendingPeriodicChecks(
        [{ id: equipmentId, inspectionType }],
        `${sessionMode}:${lastRegisteredCheck?.pendingSync ?? ''}`,
    );
    const awaitingSync = isPeriodicCheckAwaitingSync(
        pending,
        loaded,
        equipmentId,
        inspectionType,
        lastRegisteredCheck?.pendingSync,
    ) || (!loaded && sessionMode === 'submitted_pending');

    const lastRegisteredMessage = useMemo(() => {
        if (!lastRegisteredCheck?.date) {
            return null;
        }
        if (awaitingSync) {
            return t('pendingSync');
        }
        const d = moment(lastRegisteredCheck.date);
        if (isDraftStatus(lastRegisteredCheck.status)) {
            return tDetails('periodicCheckDraft', { date: d });
        }
        return d.isSame(moment(), 'day')
            ? tDetails('periodicCheckToday')
            : tDetails('periodicCheckPast', {date: d});
    }, [awaitingSync, lastRegisteredCheck?.date, lastRegisteredCheck?.status, t, tDetails]);

    const lastRegisteredIsToday = useMemo(
        () =>
            !!lastRegisteredCheck?.date
            && !isDraftStatus(lastRegisteredCheck.status)
            && !awaitingSync
            && moment(lastRegisteredCheck.date).isSame(moment(), 'day'),
        [awaitingSync, lastRegisteredCheck?.date, lastRegisteredCheck?.status]
    );

    return (
        <div className={classes['header']}>
            <div className={classes['title']}>{t(`${inspectionType}.title`)}</div>
            {(lastRegisteredCheck && lastRegisteredCheck.id) &&
            <>
                <div className={classes['last-registered']}>{t('lastRegistered')}</div>
                <div className={classes['user']}>{lastRegisteredCheck.userName}</div>
                {lastRegisteredCheck.date && (
                    <div className={classes['date-line']}>
                        {t('date', {date: lastRegisteredCheck.date})}
                    </div>
                )}
                {lastRegisteredMessage && (
                    lastRegisteredIsToday ? (
                        <div className={classes['date-wrap']}>
                            {lastRegisteredMessage}
                            <Icon name={'check'} className={classes['green-icon']} size={'14'} />
                        </div>
                    ) : (
                        <div className={classes['date-past-message']}>{lastRegisteredMessage}</div>
                    )
                )}
            </>}
        </div>
    );
};

export default Header
